import React, { useState } from 'react';
import { db } from '../../config/firebase';
import { doc, collection, setDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';

const GenerateId = ({ username, recentText, accuracyScore }) => {
  const [loading, setLoading] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateTokenId = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let tokenId = '';
    for (let i = 0; i < 6; i++) {
      tokenId += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return tokenId;
  };

  const handleGenerate = async (type) => {
    setLoading(true);
    setCopied(false);
    
    let uniqueTokenId = '';
    let tokenExists = true;

    try {
      // Check for unique token ID
      while (tokenExists) {
        uniqueTokenId = generateTokenId();
        const userDocRef = doc(db, 'users', username);
        const tokensCollectionRef = collection(userDocRef, 'tokens');
        const tokenQuery = query(tokensCollectionRef, where('tokenId', '==', uniqueTokenId));
        const querySnapshot = await getDocs(tokenQuery);
        tokenExists = !querySnapshot.empty;
      }

      const userDocRef = doc(db, 'users', username);
      const tokensCollectionRef = collection(userDocRef, 'tokens');
      const tokenDocRef = doc(tokensCollectionRef, uniqueTokenId);

      if (type === 'ai-only') {
        await setDoc(tokenDocRef, {
          tokenId: uniqueTokenId,
          text: recentText,
          accuracy: accuracyScore * 100,
          createdAt: serverTimestamp()
        });
      } else {
        const res = await axios.post('https://parentproxy.pythonanywhere.com/check', { text: recentText });
        const plagiarismScore = res.data.average_similarity;
        
        await setDoc(tokenDocRef, {
          tokenId: uniqueTokenId,
          text: recentText,
          accuracy: accuracyScore,
          plagiarism: plagiarismScore,
          createdAt: serverTimestamp(),
          plagiarismCheck: true
        });
      }

      const link = `https://synthetica-3bf2d.web.app/token/${username}/${uniqueTokenId}`;
      setGeneratedUrl(link);
      setDialogOpen(true); // Open modal with link for user to copy safely on iOS
    } catch (e) {
      console.error("Error adding document: ", e);
      alert('Error generating token');
    } finally {
      setLoading(false);
    }
  };

  // Synchronous clipboard write triggered directly by user tap
  const handleCopy = async () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(generatedUrl);
        setCopied(true);
        return;
      } catch (err) {
        console.error("Clipboard API failed: ", err);
      }
    }

    // Fallback for older iOS Safari versions
    const textArea = document.createElement('textarea');
    textArea.value = generatedUrl;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopied(true);
    } catch (err) {
      console.error('Fallback copy failed:', err);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  return (
    <div style={{ display: 'grid' }}>
      <Button
        variant="contained"
        disabled={loading}
        sx={{
          background: 'linear-gradient(90deg, rgba(144,74,216,1) 23%, rgba(65,148,213,1) 92%)',
          color: '#fff',
          '&:hover': {
            background: 'linear-gradient(90deg, rgba(144,74,216,1) 23%, rgba(65,148,213,1) 92%)',
          },
        }}
        onClick={() => handleGenerate('ai-only')}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Generate ID'}
      </Button>

      {/* Result Dialog - iOS Safari Compatible */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Verification ID Generated</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={generatedUrl}
            slotProps={{ input: { readOnly: true } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Close
          </Button>
          <Button onClick={handleCopy} variant="contained" color={copied ? "success" : "primary"}>
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GenerateId;